/**
 * collection-filters.js — Urdile Shopify Theme
 * FacetFiltersForm — filtros de colección con AJAX
 */

'use strict';

class FacetFiltersForm extends HTMLElement {
  connectedCallback() {
    this._bindEvents();
    this._filterData = [];
  }

  _bindEvents() {
    // Cambio en checkboxes o selects de filtro
    this.addEventListener('change', this._onFilterChange.bind(this));

    // Botones de chips activos para remover filtro
    document.querySelectorAll('.filter-chip').forEach((chip) => {
      chip.addEventListener('click', this.onActiveFilterClick.bind(this));
    });

    // Sort select (puede estar fuera del form)
    const sortSelect = document.querySelector('.collection__sort-select');
    if (sortSelect) {
      sortSelect.addEventListener('change', this._onSortChange.bind(this));
    }

    // Mobile filter drawer toggle
    const filterToggle = document.querySelector('.collection__filter-toggle');
    const filterDrawer = document.querySelector('.facets-drawer');
    if (filterToggle && filterDrawer) {
      filterToggle.addEventListener('click', () => {
        filterDrawer.classList.toggle('facets-drawer--open');
      });

      filterDrawer.querySelector('.facets-drawer__close')?.addEventListener('click', () => {
        filterDrawer.classList.remove('facets-drawer--open');
      });

      filterDrawer.querySelector('.facets-drawer__overlay')?.addEventListener('click', () => {
        filterDrawer.classList.remove('facets-drawer--open');
      });
    }

    // Facet group toggles (collapsible)
    document.querySelectorAll('.facet-group__toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const group = toggle.closest('.facet-group');
        group && group.classList.toggle('facet-group--collapsed');
        const expanded = !group?.classList.contains('facet-group--collapsed');
        toggle.setAttribute('aria-expanded', expanded.toString());
      });
    });
  }

  _onFilterChange(evt) {
    const form = evt.target.closest('form');
    if (!form) return;
    this.onSubmitForm(evt, form);
  }

  _onSortChange(evt) {
    const searchParams = this._getCurrentSearchParams();
    searchParams.set('sort_by', evt.target.value);
    searchParams.delete('page');
    this.renderPage(searchParams.toString(), true, true);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const chip = event.currentTarget;
    const filterUrl = chip.dataset.url || chip.href;
    if (!filterUrl) return;

    const url = new URL(filterUrl, window.location.origin);
    const searchParams = url.searchParams;
    this.renderPage(searchParams.toString(), true, false);
  }

  onSubmitForm(event, form) {
    event.preventDefault();
    const searchParams = new URLSearchParams(new FormData(form));
    // Limpia parámetros vacíos
    const cleanParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (value) cleanParams.append(key, value);
    }
    cleanParams.delete('page');
    this.renderPage(cleanParams.toString(), true, false);
  }

  /**
   * @param {string} searchParams — query string
   * @param {boolean} updateURLHash
   * @param {boolean} scrollToTop
   */
  async renderPage(searchParams, updateURLHash = false, scrollToTop = false) {
    // Mostrar loading state
    const grid = document.querySelector('#CollectionProductGrid');
    if (grid) grid.style.opacity = '0.5';

    if (updateURLHash) {
      this.updateURL(searchParams);
    }

    try {
      const url = `${window.location.pathname}?${searchParams}&sections=collection-products,facets`;
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const data = await response.json();

      if (data['collection-products']) {
        this.renderProductGridContainer(data['collection-products']);
      }

      if (data['facets']) {
        this.renderFilters(data['facets'], null);
      }

      if (scrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('FacetFiltersForm error:', error);
    } finally {
      if (grid) grid.style.opacity = '1';
    }
  }

  /**
   * @param {string} html — HTML de la sección collection-products
   */
  renderProductGridContainer(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newGrid = doc.querySelector('#CollectionProductGrid');
    const currentGrid = document.querySelector('#CollectionProductGrid');

    if (newGrid && currentGrid) {
      currentGrid.innerHTML = newGrid.innerHTML;
    }

    // Actualizar count
    const newCount = doc.querySelector('.collection__count');
    const currentCount = document.querySelector('.collection__count');
    if (newCount && currentCount) {
      currentCount.textContent = newCount.textContent;
    }
  }

  /**
   * @param {string} html — HTML de la sección facets
   * @param {Event|null} event
   */
  renderFilters(html, event) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Actualizar sidebar de filtros
    const newFacets = doc.querySelector('.facets');
    const currentFacets = document.querySelector('.facets');
    if (newFacets && currentFacets) {
      currentFacets.innerHTML = newFacets.innerHTML;
      this._rebindFacetEvents();
    }

    // Actualizar active filter chips
    const newChips = doc.querySelector('.collection__active-filters');
    const currentChips = document.querySelector('.collection__active-filters');
    if (newChips && currentChips) {
      currentChips.innerHTML = newChips.innerHTML;
      this._rebindChipEvents();
    }
  }

  updateURL(searchParams) {
    const url = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
    history.pushState({ searchParams }, '', url);
  }

  _getCurrentSearchParams() {
    return new URLSearchParams(window.location.search);
  }

  _rebindFacetEvents() {
    document.querySelectorAll('.facet-group__toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const group = toggle.closest('.facet-group');
        group && group.classList.toggle('facet-group--collapsed');
      });
    });
  }

  _rebindChipEvents() {
    document.querySelectorAll('.filter-chip').forEach((chip) => {
      chip.addEventListener('click', this.onActiveFilterClick.bind(this));
    });
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
